import { type Notification } from '@novu/js'
import { onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import { setNotificationsUnavailable } from '@/lib/services/notification-availability.service'
import { initializeNovu } from '@/lib/services/novu.services'
import { type NotificationType } from '@/lib/types/notifications.types'
import { useCampaignParticipantsStore } from '@/stores/campaign-participants.stores'
import { useNotificationStore } from '@/stores/notifications.stores'
import { useUserStore } from '@/stores/user.stores'

export function useNovuWebsocketService() {
  const route = useRoute()
  const userStore = useUserStore()
  const notificationsStore = useNotificationStore()
  const campaignParticipantsStore = useCampaignParticipantsStore()
  let cleanupFunctions: (() => void)[] = []

  const handleNotificationReceived = ({ result }: { result: Notification }) => {
    const data = result.data as NotificationType['data']
    const campaignId = data?.events[0]?.payload.campaign.id
    if (campaignId && String(campaignId) === route.params.id) {
      campaignParticipantsStore.setHasUpdates(true)
    }

    void Promise.all([notificationsStore.fetchUnread(true), notificationsStore.fetchAll(true)])
  }

  const changeUnreadCount = ({
    result,
  }: {
    result: { total: number; severity: Record<string, number> }
  }) => {
    notificationsStore.changeUnreadCount(result.total)
  }

  const initialize = async (subscriberId: string, hash: string) => {
    setNotificationsUnavailable(false)

    try {
      const novu = initializeNovu(subscriberId, hash)
      const result = await novu.notifications.count({
        read: false,
        archived: false,
      })

      if (result?.error) {
        setNotificationsUnavailable(true)
        return
      }

      notificationsStore.changeUnreadCount(result.data?.count || 0)

      cleanupFunctions.push(
        novu.on('notifications.notification_received', handleNotificationReceived),
      )
      cleanupFunctions.push(novu.on('notifications.unread_count_changed', changeUnreadCount))
    } catch {
      setNotificationsUnavailable(true)
    }
  }

  const uninitialize = () => {
    if (cleanupFunctions.length) {
      cleanupFunctions.forEach((cleanup) => cleanup())
      cleanupFunctions = []
    }
  }

  watch(
    () => userStore.user,
    async (user) => {
      if (user) {
        await initialize(user.subscriber.id, user.subscriber.hash)
      }
    },
    { immediate: true },
  )

  onUnmounted(() => uninitialize())
}
